import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { useSession } from "../session/SessionProvider";
import { money, whenIn } from "../system/format";
import { takeHubGrant } from "./hubGrant";

// ---------------------------------------------------------------------------
// V-ETA-2b (0301). REQUESTS FROM EVENT.CLINIC.
//
// An organiser engages the agency through an event.clinic staffing request; the hub emails
// the contact a link to /enter?grant_token=…, which holds the grant in this tab only and
// sends them here. This screen spends the grant once (POST /v1/hub/claims), lists what the
// organisation has claimed, lets a named person link each request to one of its events,
// and sends the quote, priced by the server from that event's own posts (V-ETA-2a).
// Nothing here is matched or created for them.
// ---------------------------------------------------------------------------

type Role = { role: string; headcount: number; shift_start: string; shift_end: string };
type Claim = {
  requirement_id: string; engagement_id: string; requirement_version: number; roles: Role[];
  location: string | null; respond_by: string | null; event_id: string | null; event_name: string | null;
  is_closed: boolean; committed_at: string | null; confirmation_cancelled: boolean;
  quoted_at: string | null; quoted_net_minor: number | string | null; quoted_currency: string | null; quoted_vat_rate: number | string | null;
};
type EventRow = { id: string; name: string };
type Failure = { error?: { message?: string } };

export function HubRequests() {
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const [claims, setClaims] = useState<Claim[] | null>(null);
  const [events, setEvents] = useState<EventRow[]>([]);
  // The event list can be refused while the requests are not (a role without event.read):
  // the picker then says why, rather than offering a select with nothing in it.
  const [eventsRefused, setEventsRefused] = useState<string | null>(null);
  type Notice = { tone: "live" | "attention" | "failed"; text: string };
  // TWO SLOTS. The grant is spent once, so what the claim answered (how many were taken on,
  // how many were dropped) must not be overwritten by a list read that fails after it.
  const [claimed, setClaimed] = useState<Notice | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const spent = useRef(false);
  const lost = (useLocation().state as { hubLinkLost?: boolean } | null)?.hubLinkLost === true;

  const load = useCallback(async () => {
    try {
      const [c, e] = await Promise.all([
        send<{ claims?: Claim[] } & Failure>(console_.hubClaims(apiBase)),
        send<{ events?: EventRow[] } & Failure>(console_.listEvents(apiBase)),
      ]);
      if (c.status !== 200 || !c.body.claims) {
        setNotice({ tone: "failed", text: refusal(c.body.error) ?? t("Requests from event.clinic could not be read.") });
        return;
      }
      setNotice(null);
      setClaims(c.body.claims);
      setEvents(e.body.events ?? []);
      setEventsRefused(e.status !== 200 ? (refusal(e.body.error) ?? t("Your events could not be read.")) : null);
    } catch {
      setNotice({ tone: "failed", text: t("We could not reach Verbunk. Check your connection and try again.") });
    }
  }, [apiBase]);

  useEffect(() => {
    void (async () => {
      // THE GRANT IS SPENT ONCE AND FORGOTTEN, whatever the answer: it is the contact's
      // personal access, and a second attempt is a second click on the email link.
      const grant = spent.current ? null : takeHubGrant();
      spent.current = true;
      if (!grant && lost) {
        setClaimed({ tone: "attention", text: t("That event.clinic link could not be kept in this browser. Open it again from the email.") });
      }
      if (grant) {
        try {
          const r = await send<{ requests?: number; dropped?: number } & Failure>(console_.claimHubRequests(apiBase, grant));
          const dropped = r.body.dropped ?? 0;
          setClaimed(r.status === 200
            ? dropped > 0
              ? { tone: "attention", text: t("{n} request(s) from event.clinic taken on; {dropped} could not be read and were left out. Ask the organiser.", { n: r.body.requests ?? 0, dropped }) }
              : { tone: "live", text: t("{n} request(s) from event.clinic taken on.", { n: r.body.requests ?? 0 }) }
            : { tone: "attention", text: refusal(r.body.error) ?? t("That event.clinic link could not be used.") });
        } catch {
          setClaimed({ tone: "failed", text: t("We could not reach Verbunk. Check your connection and try again.") });
        }
      }
      await load();
    })();
  }, [apiBase, load, lost]);

  return (
    <section className="panel">
      <div className="panel-head">
        <h1>{t("Requests from event.clinic")}</h1>
        <p className="muted">{t("An organiser asks for staff on event.clinic; link the request to one of your events, then quote it.")}</p>
      </div>
      {[claimed, notice].map((n, i) =>
        n ? (
          <div key={i} className="form-message" role="status" aria-live="polite">
            <StatusChip tone={n.tone} label={n.text} />
          </div>
        ) : null,
      )}
      {claims === null ? (
        notice ? null : <p className="muted">{t("Loading…")}</p>
      ) : claims.length === 0 ? (
        <p className="muted">{t("No requests yet. They arrive from the link in an event.clinic email.")}</p>
      ) : (
        <ul className="plain-list">
          {claims.map((c) => (
            <ClaimCard key={c.requirement_id} claim={c} events={events} eventsRefused={eventsRefused} onChanged={load} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ClaimCard({ claim, events, eventsRefused, onChanged }: {
  claim: Claim; events: EventRow[]; eventsRefused: string | null; onChanged: () => Promise<void>;
}) {
  const { apiBase, me } = useSession();
  // A shift happens where the venue is (format.ts, whenIn): the event this request is
  // linked to is shown in the organisation's clock, so the request is too.
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const { t, refusal } = useT();
  const [eventId, setEventId] = useState("");
  const [vat, setVat] = useState("27");
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // D97-3: a request holds one quote. Another goes only when the person chooses to revise it.
  const [revising, setRevising] = useState(false);
  // The parent's list is the first page of events; a search reaches the rest (Sweep S15's `q`).
  const [q, setQ] = useState("");
  const [found, setFound] = useState<EventRow[] | null>(null);
  const key = useRef<string | null>(null);

  async function find() {
    setBusy(true);
    setNotice(null);
    try {
      const r = await send<{ events?: EventRow[] } & Failure>(console_.listEvents(apiBase, { q: q.trim() }));
      if (r.status === 200) {
        setFound(r.body.events ?? []);
        setEventId("");
      } else {
        setNotice(refusal(r.body.error) ?? t("Your events could not be read."));
      }
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  async function link() {
    if (!eventId) return;
    setBusy(true);
    setNotice(null);
    try {
      const r = await send<Failure>(console_.linkHubClaim(apiBase, claim.engagement_id, eventId));
      if (r.status !== 200) setNotice(refusal(r.body.error) ?? t("That could not be saved. Try again."));
      else await onChanged();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  async function quote() {
    if (!claim.event_id || !until) return;
    setBusy(true);
    setNotice(null);
    // One key per intent, held across retries: a retry is the same quote (the hub replays it).
    key.current ??= crypto.randomUUID();
    try {
      const r = await send<{ net_minor?: number; currency?: string } & Failure>(console_.sendHubQuote(apiBase, claim.event_id, {
        requirement: claim.requirement_id,
        vat_rate: Number(vat),
        valid_until: `${until}T23:59:59Z`,
        idempotency_key: key.current,
        revision: revising,
      }));
      if (r.status === 200) {
        key.current = null;
        setRevising(false);
        setNotice(t("Quote sent to event.clinic: {amount} net.", { amount: money(r.body.net_minor ?? 0, r.body.currency ?? null) }));
        await onChanged();
      } else {
        setNotice(refusal(r.body.error) ?? t("That could not be saved. Try again."));
      }
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="stack">
      <ul className="plain-list">
        {claim.roles.map((r, i) => (
          <li key={i}>
            <strong>{r.role}</strong> × {r.headcount} · {whenIn(r.shift_start, orgZone)} – {whenIn(r.shift_end, orgZone)}
          </li>
        ))}
      </ul>
      {claim.is_closed ? <StatusChip tone="neutral" label={t("Withdrawn or cancelled by the organiser")} /> : null}
      {!claim.is_closed && claim.confirmation_cancelled ? (
        <StatusChip tone="attention" label={t("The organiser cancelled the confirmation. You can quote again.")} />
      ) : null}
      {!claim.is_closed && claim.committed_at ? (
        <StatusChip tone="live" label={t("Confirmed by the organiser on {when}", { when: whenIn(claim.committed_at, orgZone) })} />
      ) : null}
      {!claim.is_closed && claim.requirement_version > 1 ? (
        <StatusChip tone="attention" label={t("Changed by the organiser (version {n})", { n: claim.requirement_version })} />
      ) : null}
      {claim.location ? <p className="muted">{claim.location}</p> : null}
      {claim.respond_by ? <p className="muted">{t("Answer by {when}", { when: whenIn(claim.respond_by, orgZone) })}</p> : null}
      {claim.is_closed ? null : claim.event_id ? (
        <>
          <p>
            {t("Linked to")} <Link to={`/console/events/${claim.event_id}`}>{claim.event_name ?? claim.event_id}</Link>
          </p>
          {claim.quoted_at ? (
            <p>
              {t("Quoted on {when} for {amount} net, VAT {vat}%", {
                when: whenIn(claim.quoted_at, orgZone),
                amount: money(Number(claim.quoted_net_minor ?? 0), claim.quoted_currency),
                vat: Number(claim.quoted_vat_rate ?? 0),
              })}
            </p>
          ) : null}
          {claim.committed_at ? null : claim.quoted_at && !revising ? (
            <div className="row">
              <button type="button" className="btn btn-secondary" onClick={() => setRevising(true)}>
                {t("Send a revised quote")}
              </button>
            </div>
          ) : (
          <div className="row">
            <label>
              {t("VAT rate (%)")}{" "}
              <input type="number" min={0} max={100} step="0.1" value={vat} onChange={(e) => setVat(e.target.value)} />
            </label>
            <label>
              {t("Valid until")}{" "}
              <input type="date" min={new Date().toISOString().slice(0, 10)} value={until} onChange={(e) => setUntil(e.target.value)} />
            </label>
            <button type="button" className="btn" disabled={busy || !until || vat === ""} onClick={() => void quote()}>
              {busy ? t("Sending…") : revising ? t("Send the revised quote") : t("Send quote")}
            </button>
            {revising ? (
              <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => setRevising(false)}>
                {t("Cancel")}
              </button>
            ) : null}
          </div>
          )}
        </>
      ) : eventsRefused ? (
        <div className="form-message" role="status">
          <StatusChip tone="attention" label={t("Not linked")} />
          <span>{eventsRefused}</span>
        </div>
      ) : (
        <div className="row">
          <label>
            {t("Search events")}{" "}
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void find(); } }} />
          </label>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void find()}>
            {t("Search")}
          </button>
          <label>
            {t("Your event")}{" "}
            <select value={eventId} onChange={(e) => setEventId(e.target.value)}>
              <option value="">{found && found.length === 0 ? t("No event matches") : t("Choose an event")}</option>
              {(found ?? events).map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </label>
          <button type="button" className="btn" disabled={busy || !eventId} onClick={() => void link()}>
            {t("Link to this event")}
          </button>
        </div>
      )}
      {notice ? <p className="form-message" role="status" aria-live="polite">{notice}</p> : null}
    </li>
  );
}

